import React, { FC } from 'react';
import { Typography, Box } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type {
  ChatV2QuickActionCategory,
  ChatV2QuickActionItem,
  ChatV2QuickActionSubItem,
} from '../types';

import './QuickActionCategoryList.scss';

interface QuickActionCategoryListProps {
  category: ChatV2QuickActionCategory;
  onItemPress: (item: ChatV2QuickActionItem) => void;
  onSubItemPress?: (subItem: ChatV2QuickActionSubItem) => void;
  expandedItemId?: string | null;
}

const fadeInDownVariants = {
  initial: {
    opacity: 0,
    y: -10,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
};

const QuickActionCategoryList: FC<QuickActionCategoryListProps> = ({
  category,
  onItemPress,
  onSubItemPress,
  expandedItemId = null,
}) => {
  const { t } = useTranslation();

  const renderTitle = () => {
    const displayTitle = t(`task.ai.quickActions.categories.${category.id}.detailTitle`, {
      defaultValue: category.detailTitle ?? category.title,
    });
    const words = displayTitle.split(' ');
    const firstWord = words[0];
    const remainingWords = words.slice(1).join(' ');

    return (
      <Typography variant="h3" className="quick-action-category-list__headline">
        <span className="quick-action-category-list__headline__first-word">{firstWord}</span>
        {remainingWords && (
          <span className="quick-action-category-list__headline__remaining-words">
            {' '}
            {remainingWords}
          </span>
        )}
      </Typography>
    );
  };

  return (
    <Box className="quick-action-category-list">
      <Box className="quick-action-category-list__header-row">{renderTitle()}</Box>

      <Box className="quick-action-category-list__items-wrapper">
        {category.items.map((item, index) => {
          const hasSubItems = Boolean(item.subItems && item.subItems.length > 0);
          const isExpanded = expandedItemId === item.id;

          return (
            <motion.div
              key={item.id}
              className="quick-action-category-list__item-group"
              variants={fadeInDownVariants}
              initial="initial"
              animate="animate"
              transition={{
                duration: 0.28,
                delay: index * 0.06,
                ease: 'easeOut',
              }}
            >
              <button
                className="quick-action-category-list__item"
                onClick={() => onItemPress(item)}
                type="button"
              >
                <Box className="quick-action-category-list__item-content">
                  {item.icon}
                  <Typography variant="body1" className="quick-action-category-list__item-label">
                    {t(`task.ai.quickActions.items.${item.id}`, {
                      defaultValue: item.label,
                    })}
                  </Typography>
                  {item.badgeLabel && (
                    <span className="quick-action-category-list__item-badge">
                      {t(`task.ai.quickActions.badges.${item.badgeLabel}`, {
                        defaultValue: item.badgeLabel,
                      })}
                    </span>
                  )}
                </Box>
              </button>
              <AnimatePresence>
                {hasSubItems && isExpanded && (
                  <motion.div
                    className="quick-action-category-list__sub-items"
                    initial={{ opacity: 0, height: 0, y: -6 }}
                    animate={{ opacity: 1, height: 'auto', y: 0 }}
                    exit={{ opacity: 0, height: 0, y: -6 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    {item.subItems?.map((subItem, subIndex) => (
                      <motion.button
                        key={subItem.id}
                        className="quick-action-category-list__sub-item"
                        type="button"
                        onClick={() => onSubItemPress?.(subItem)}
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.18, delay: subIndex * 0.04, ease: 'easeOut' }}
                      >
                        <Typography
                          variant="body2"
                          className="quick-action-category-list__sub-item-label"
                        >
                          {t(`task.ai.quickActions.subItems.${subItem.id}`, {
                            defaultValue: subItem.label,
                          })}
                        </Typography>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </Box>
    </Box>
  );
};

export default QuickActionCategoryList;
